import { Add, Location, Partnership, TrashCan } from '@carbon/icons-react';
import { Button, ComboBox, Dropdown, Tag, TextInput, Toggle } from '@carbon/react';
import { asProblem, FormModal, hasPermission, ListTable, PageHeader, problemMessage, RowAction, StatusTag, useAdmin, useLocalPages } from '@pos/admin-ui';
import { ConfirmDialog } from '@pos/design-system';
import { fold, useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorNotice } from '../common/Notices';
import { brandsKey, legalEntitiesApi, legalEntitiesKey, listAllPages, providerApi, scopeApis, type ProviderAccount } from '../delivery/deliveryApi';
import {
  assignProfile,
  deleteProfile,
  listProfiles,
  PAYMENTS_MANAGE,
  profilesKey,
  salesChannelsApi,
  salesChannelsKey,
  saveProfile,
  setProfileAccounts,
  unassignProfile,
  type PaymentProfile,
} from './paymentProfilesApi';

const payment = providerApi('payment');
const storesKey = ['ho', 'org', 'stores', 'all'] as const;
type Store = { id: string; name: string; brandId?: string | null; legalEntityId?: string | null };

/** The lists every dialog of the page picks from. */
function usePicks() {
  const entities = useQuery({ queryKey: legalEntitiesKey, queryFn: () => listAllPages(legalEntitiesApi) });
  const brands = useQuery({ queryKey: brandsKey, queryFn: () => listAllPages(scopeApis.brands) });
  const stores = useQuery({ queryKey: storesKey, queryFn: () => listAllPages(scopeApis.stores) as Promise<Store[]> });
  const channels = useQuery({ queryKey: salesChannelsKey, queryFn: () => listAllPages(salesChannelsApi) });
  const providers = useQuery({ queryKey: payment.providersKey, queryFn: payment.listProviders });
  const accounts = useQuery({ queryKey: payment.accountsKey, queryFn: payment.listAccounts });
  return { entities, brands, stores, channels, providers, accounts };
}

/**
 * Payment profiles (ADR 0074): a company's merchant accounts, one per payment provider, and where they take payments: a
 * brand, a store, a sales channel, or a mix. The most specific wins (store, then channel, then brand), and a store only ever
 * uses a profile of its own company, so each store and channel can pay into its own accounts.
 */
export function PaymentProfilesPage() {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canEdit = hasPermission(session, PAYMENTS_MANAGE);
  const queryClient = useQueryClient();
  const profiles = useQuery({ queryKey: profilesKey, queryFn: listProfiles });
  const picks = usePicks();
  const [editing, setEditing] = useState<PaymentProfile | 'new'>();
  const [accountsOf, setAccountsOf] = useState<PaymentProfile>();
  const [placesOf, setPlacesOf] = useState<PaymentProfile>();
  const [deleting, setDeleting] = useState<PaymentProfile>();
  const [search, setSearch] = useState('');
  const query = fold(search.trim());
  const pages = useLocalPages((profiles.data ?? []).filter((p) => !query || fold(`${p.code} ${p.name}`).includes(query)));
  const refresh = () => queryClient.invalidateQueries({ queryKey: profilesKey });
  const remove = useMutation({
    mutationFn: (p: PaymentProfile) => deleteProfile(p.id),
    onSuccess: async () => {
      setDeleting(undefined);
      await refresh();
    },
  });

  const entityName = (id: string) => picks.entities.data?.find((e) => e.id === id)?.legalName ?? '—';
  const accountName = (id: string) => picks.accounts.data?.find((a) => a.id === id)?.name ?? '—';
  const providerName = (code: string) => picks.providers.data?.find((p) => p.code === code)?.name ?? code;
  const live = (p: PaymentProfile) => profiles.data?.find((x) => x.id === p.id) ?? p;

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('payments.profiles.title')} description={t('payments.profiles.intro')} />
      {picks.entities.error && <ErrorNotice title={problemMessage(t, asProblem(picks.entities.error))} onRetry={() => void picks.entities.refetch()} />}
      <ListTable<PaymentProfile>
        ariaLabel={t('payments.profiles.title')}
        loading={profiles.isLoading}
        error={profiles.error ? problemMessage(t, asProblem(profiles.error)) : undefined}
        onRetry={() => void profiles.refetch()}
        rows={pages.rows}
        pagination={pages.pagination}
        // As on every list screen: search and Create in the table's toolbar, a row opens for editing.
        search={{
          label: t('admin.search'),
          onChange: (value) => {
            setSearch(value);
            pages.reset();
          },
        }}
        toolbarActions={
          canEdit ? (
            <Button renderIcon={Add} onClick={() => setEditing('new')}>
              {t('admin.create')}
            </Button>
          ) : undefined
        }
        onRowClick={canEdit ? (p) => setEditing(p) : undefined}
        empty={search ? undefined : { heading: t('admin.emptyTitle'), description: t('payments.profiles.empty') }}
        columns={[
          { key: 'name', header: t('payments.profiles.columns.name'), render: (p) => `${p.name} (${p.code})` },
          { key: 'legalEntityId', header: t('payments.profiles.columns.entity'), render: (p) => entityName(p.legalEntityId) },
          {
            key: 'accounts',
            header: t('payments.profiles.columns.accounts'),
            render: (p) =>
              p.accounts.length
                ? p.accounts.map((a) => (
                    <Tag key={a.id} type="cool-gray" size="sm">
                      {providerName(a.providerCode)}: {accountName(a.providerAccountId)}
                    </Tag>
                  ))
                : t('payments.profiles.noAccounts'),
          },
          { key: 'assignments', header: t('payments.profiles.columns.places'), render: (p) => t('payments.profiles.placeCount', { count: p.assignments.length }) },
          {
            key: 'isActive',
            header: t('payments.profiles.columns.status'),
            render: (p) => <StatusTag status={p.isActive ? 'active' : 'inactive'}>{t(p.isActive ? 'payments.profiles.active' : 'payments.profiles.inactive')}</StatusTag>,
          },
        ]}
        rowActions={
          canEdit
            ? (p) => (
                <>
                  <RowAction action={t('payments.profiles.accounts')} record={p.name} icon={Partnership} onClick={() => setAccountsOf(p)} />
                  <RowAction action={t('payments.profiles.places')} record={p.name} icon={Location} onClick={() => setPlacesOf(p)} />
                  <RowAction action={t('admin.delete')} record={p.name} icon={TrashCan} onClick={() => setDeleting(p)} />
                </>
              )
            : undefined
        }
      />
      {editing && (
        <ProfileDialog
          profile={editing === 'new' ? undefined : editing}
          picks={picks}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            void refresh();
          }}
        />
      )}
      {accountsOf && <AccountsDialog profile={live(accountsOf)} picks={picks} onClose={() => setAccountsOf(undefined)} onSaved={() => void refresh()} />}
      {placesOf && <PlacesDialog profile={live(placesOf)} picks={picks} onClose={() => setPlacesOf(undefined)} onChanged={() => void refresh()} />}
      <ConfirmDialog
        open={!!deleting}
        title={t('payments.profiles.deleteTitle')}
        confirmLabel={t('common.delete')}
        busy={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(undefined)}
      >
        {t('payments.profiles.deleteBody')}
      </ConfirmDialog>
    </section>
  );
}

type Picks = ReturnType<typeof usePicks>;

function ProfileDialog({ profile, picks, onClose, onSaved }: { profile?: PaymentProfile; picks: Picks; onClose(): void; onSaved(): void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState(profile?.code ?? '');
  const [name, setName] = useState(profile?.name ?? '');
  const [entity, setEntity] = useState(profile?.legalEntityId);
  const [active, setActive] = useState(profile?.isActive ?? true);
  const save = useMutation({
    mutationFn: () => saveProfile(profile?.id, { code, name, legalEntityId: entity ?? '', isActive: active, version: profile ? Number(profile.version) : null }),
    onSuccess: onSaved,
  });
  const entities = picks.entities.data ?? [];
  return (
    <FormModal
      size="sm"
      title={profile ? t('payments.profiles.editTitle') : t('payments.profiles.add')}
      disabled={!code.trim() || !name.trim() || !entity}
      pending={save.isPending}
      error={save.error ? problemMessage(t, asProblem(save.error)) : null}
      onSubmit={() => save.mutate()}
      onClose={onClose}
    >
      <TextInput id="profile-code" labelText={t('payments.profiles.code')} value={code} onChange={(e) => setCode(e.target.value)} />
      <TextInput id="profile-name" labelText={t('payments.profiles.name')} value={name} onChange={(e) => setName(e.target.value)} />
      <ComboBox
        id="profile-entity"
        titleText={t('payments.profiles.entity')}
        helperText={t('payments.profiles.entityHelp')}
        items={entities}
        itemToString={(e) => e?.legalName ?? ''}
        selectedItem={entities.find((e) => e.id === entity) ?? null}
        onChange={({ selectedItem }) => setEntity(selectedItem?.id)}
      />
      <Toggle id="profile-active" labelText={t('payments.profiles.columns.status')} labelA={t('payments.profiles.inactive')} labelB={t('payments.profiles.active')}
        toggled={active} onToggle={setActive} />
    </FormModal>
  );
}

/** One account per provider, among the provider's accounts of the profile's company; "none" takes the provider off. */
function AccountsDialog({ profile, picks, onClose, onSaved }: { profile: PaymentProfile; picks: Picks; onClose(): void; onSaved(): void }) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<Record<string, string | undefined>>(
    Object.fromEntries(profile.accounts.map((a) => [a.providerCode, a.providerAccountId])),
  );
  const save = useMutation({
    mutationFn: () =>
      setProfileAccounts(profile.id, Object.entries(chosen).flatMap(([providerCode, providerAccountId]) => (providerAccountId ? [{ providerCode, providerAccountId }] : []))),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const none = { id: '', name: t('payments.profiles.noAccount') } as ProviderAccount;
  return (
    <FormModal
      size="sm"
      title={t('payments.profiles.accountsTitle', { name: profile.name })}
      pending={save.isPending}
      error={save.error ? problemMessage(t, asProblem(save.error)) : null}
      onSubmit={() => save.mutate()}
      onClose={onClose}
    >
      {(picks.providers.data ?? []).map((provider) => {
        const options = [none, ...(picks.accounts.data ?? []).filter((a) => a.providerCode === provider.code && a.legalEntityId === profile.legalEntityId && a.isActive)];
        return (
          <Dropdown
            key={provider.code}
            id={`profile-account-${provider.code}`}
            titleText={provider.name}
            label={t('payments.profiles.noAccount')}
            items={options}
            itemToString={(a) => a?.name ?? ''}
            selectedItem={options.find((a) => a.id === (chosen[provider.code] ?? '')) ?? none}
            onChange={({ selectedItem }) => setChosen((c) => ({ ...c, [provider.code]: selectedItem?.id || undefined }))}
          />
        );
      })}
    </FormModal>
  );
}

/** Where the profile takes payments: each row a brand, a store and a channel, any of them "all". */
function PlacesDialog({ profile, picks, onClose, onChanged }: { profile: PaymentProfile; picks: Picks; onClose(): void; onChanged(): void }) {
  const { t } = useTranslation();
  const [brand, setBrand] = useState<string>();
  const [store, setStore] = useState<string>();
  const [channel, setChannel] = useState<string>();
  const add = useMutation({
    mutationFn: () => assignProfile(profile.id, { brandId: brand ?? null, storeId: store ?? null, salesChannelId: channel ?? null }),
    onSuccess: () => {
      setBrand(undefined);
      setStore(undefined);
      setChannel(undefined);
      onChanged();
    },
  });
  const remove = useMutation({ mutationFn: (assignmentId: string) => unassignProfile(profile.id, assignmentId), onSuccess: onChanged });
  const brands = picks.brands.data ?? [];
  // Only the profile's company's stores (of the brand chosen): a store's money never goes to another company's account.
  const stores = (picks.stores.data ?? []).filter((s) => (!s.legalEntityId || s.legalEntityId === profile.legalEntityId) && (!brand || s.brandId === brand));
  const channels = picks.channels.data ?? [];
  const all = t('payments.profiles.all');
  const name = (list: { id: string; name: string }[], id?: string | null) => (id ? (list.find((x) => x.id === id)?.name ?? '—') : all);
  return (
    <FormModal
      size="md"
      title={t('payments.profiles.placesTitle', { name: profile.name })}
      primaryText={t('payments.profiles.addPlace')}
      pending={add.isPending}
      error={(add.error ?? remove.error) ? problemMessage(t, asProblem(add.error ?? remove.error)) : null}
      onSubmit={() => add.mutate()}
      onClose={onClose}
    >
      {profile.assignments.length === 0 ? (
        <p>{t('payments.profiles.noPlaces')}</p>
      ) : (
        <ul>
          {profile.assignments.map((a) => (
            <li key={a.id}>
              {t('payments.profiles.place', {
                brand: name(brands, a.brandId),
                store: name(picks.stores.data ?? [], a.storeId),
                channel: name(channels, a.salesChannelId),
              })}{' '}
              <Button kind="ghost" size="sm" hasIconOnly renderIcon={TrashCan} iconDescription={t('payments.profiles.removePlace')} onClick={() => remove.mutate(a.id)} />
            </li>
          ))}
        </ul>
      )}
      <ComboBox id="place-brand" titleText={t('payments.profiles.brand')} placeholder={all} items={brands} itemToString={(b) => b?.name ?? ''}
        selectedItem={brands.find((b) => b.id === brand) ?? null} onChange={({ selectedItem }) => setBrand(selectedItem?.id)} />
      <ComboBox id="place-store" titleText={t('payments.profiles.store')} placeholder={all} items={stores} itemToString={(s) => s?.name ?? ''}
        selectedItem={stores.find((s) => s.id === store) ?? null} onChange={({ selectedItem }) => setStore(selectedItem?.id)} />
      <ComboBox id="place-channel" titleText={t('payments.profiles.channel')} placeholder={all} items={channels} itemToString={(c) => c?.name ?? ''}
        selectedItem={channels.find((c) => c.id === channel) ?? null} onChange={({ selectedItem }) => setChannel(selectedItem?.id)} />
    </FormModal>
  );
}
